import * as React from "react";
import classNames from "classNames";
import { MoneyTextBoxEditor } from "../../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { IncomeArrangementType } from "../../Resource/Content";
import { DrawdownPaymentSourceAllocation } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownPaymentSourceAllocation";
import { Money } from "../../../../../../../Wrap/Core/DataTypes/Money";
import { isNullOrEmpty } from "../../../../../../../react-core/Utils/Arrays";
import { PensionArrangementDetail } from "../../../../../../../Wrap/Models/Models/Processes/Drawdown/PensionArrangementDetail";
import { CappedPensionArrangementDetail } from "../../../../../../../Wrap/Models/Models/Processes/Drawdown/CappedPensionArrangementDetail";
import { CheckboxGroup } from "../../../../../../Shared/CheckboxGroup/CheckboxGroup";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";

export interface OneOffIncomeTableProps extends IProcessComponentProps {
    tableTitle: string;
    cssWrapper: string;
    ArrangementType: IncomeArrangementType;
    data: any;
    arrangementSelection: DrawdownPaymentSourceAllocation[];
    onChange: (newData: DrawdownPaymentSourceAllocation[]) => void;
}

export class OneOffIncomeTable extends ProcessComponent<OneOffIncomeTableProps, {}> {
    public render(): JSX.Element {
        const sharedColumns = this.getSharedColumns();
        const cappedColumns = this.getCappedColumns();
        const cssWrapper = classNames("one-off-income-table", this.props.cssWrapper);
        return (
            <>
                <h3 className="mb-11">{this.props.tableTitle}</h3>
                <table className={cssWrapper}>
                    <tbody>
                        {sharedColumns.map((row, index) => {
                            return (
                                <tr key={row.key + index}>
                                    <td className="text-left"> {<h4 className="mb-11">{row.title}</h4>}</td>
                                    {this.props.data.map((item, index) => {
                                        return row.render(item.PensionArrangementDetail);
                                    })}
                                </tr>
                            );
                        })}
                        {this.isCapped() &&
                            cappedColumns.map((row, index) => {
                                return (
                                    <tr key={row.key + index}>
                                        <td className="text-left"> {<h4 className="mb-11">{row.title}</h4>}</td>
                                        {this.props.data.map((item, index) => {
                                            return row.render(item);
                                        })}
                                    </tr>
                                );
                            })}
                        <tr key="MaximumOneOffIncomePayment">
                            <td className="text-left">
                                {" "}
                                <h4 className="mb-11">Maximum one-off income payment</h4>
                            </td>
                            {this.props.data.map((item, index) => {
                                return <td>{MoneyDisplayHelper.getMoneyFormat(item.MaximumIncomeRemaining)}</td>;
                            })}
                        </tr>
                        <tr key="OneOffIncomePayment">
                            <td className="text-left">
                                {" "}
                                <h4 className="mb-11">One-off income payment</h4>
                            </td>
                            {this.props.data.map((item, index) => this.getOneOffIncomePaymentDataColumn(item))}
                        </tr>
                    </tbody>
                    <tfoot></tfoot>
                </table>
            </>
        );
    }

    private getOneOffIncomePaymentDataColumn(data): JSX.Element {
        let selectedArrangement = this.getSelectedArrangement(data.PensionArrangementDetail);

        if (!selectedArrangement) {
            selectedArrangement = {
                Amount: {
                    Amount: 0,
                    Currency: CurrencyCode.GBP,
                },
                FullAmount: false,
                ArrangementId: data.PensionArrangementDetail.ArrangementId,
            };
        }

        return (
            <td>
                <MoneyTextBoxEditor
                    required={"Please enter single payment"}
                    value={selectedArrangement.Amount?.Amount}
                    currency={selectedArrangement.Amount?.Currency}
                    label={""}
                    disabled={selectedArrangement?.FullAmount}
                    onChange={(v) =>
                        this.onArrangementValueChange(v, selectedArrangement.ArrangementId, data.MaximumIncomeRemaining)
                    }
                    maxNumberOfDecimalPlaces={{
                        errorMessage: "only allow two decimal places",
                        value: 2,
                    }}
                    validationRules={[
                        {
                            errorMessage: "Value can not greater than maximum one-off income payment",
                            validator: (v) => v <= data.MaximumIncomeRemaining?.Amount.toFixed(2),
                        },
                        {
                            errorMessage: "Value can not be less than 0",
                            validator: (v) => v >= 0,
                        },
                    ]}
                />
                {!this.isCapped() && (
                    <CheckboxGroup
                        type="block"
                        options={[
                            {
                                label: "Take Full Value",
                                value: selectedArrangement?.FullAmount,
                                onChange: (v) => {
                                    if (v) {
                                        this.onArrangementValueChange(
                                            Number(data.MaximumIncomeRemaining?.Amount.toFixed(2)),
                                            selectedArrangement.ArrangementId,
                                            data.MaximumIncomeRemaining
                                        );
                                    } else {
                                        this.onArrangementValueChange(
                                            0,
                                            selectedArrangement.ArrangementId,
                                            data.MaximumIncomeRemaining
                                        );
                                    }
                                },
                            },
                        ]}
                    />
                )}
            </td>
        );
    }

    private onArrangementValueChange(newValue: number, arrangementId: number, fullValue: Money): void {
        const unchangedArrangements = this.props.arrangementSelection.filter((x) => x.ArrangementId != arrangementId);
        const newArrangement: DrawdownPaymentSourceAllocation = {
            ArrangementId: arrangementId,
            Amount: {
                Amount: newValue,
                Currency: fullValue?.Currency,
            },
            FullAmount: this.isCapped() ? false : fullValue?.Amount == newValue,
        };
        unchangedArrangements.push(newArrangement);

        this.props.onChange(unchangedArrangements);
    }

    private getSelectedArrangement(arrangementDetail: PensionArrangementDetail): DrawdownPaymentSourceAllocation {
        if (isNullOrEmpty(this.props.arrangementSelection)) {
            return null;
        }
        return this.props.arrangementSelection.find((x) => x.ArrangementId == arrangementDetail?.ArrangementId);
    }

    private isCapped(): boolean {
        return this.props.ArrangementType == IncomeArrangementType.Capped;
    }

    private getSharedColumns(): any {
        return [
            {
                title: this.isCapped() ? "Arrangement ID" : "From",
                key: "From",
                render: (data: PensionArrangementDetail): JSX.Element => {
                    return <td>{data.ArrangementId}</td>;
                },
            },
            {
                title: "Amount",
                key: "Amount",
                render: (data: PensionArrangementDetail): JSX.Element => {
                    return <td>{MoneyDisplayHelper.getMoneyFormat(data.Amount)}</td>;
                },
            },
            {
                title: "Type",
                key: "Type",
                render: (data: PensionArrangementDetail): JSX.Element => {
                    return <td>{data.IsInherited ? "Inherited" : "Member"}</td>;
                },
            },
            {
                title: "Taxable",
                key: "Taxable",
                render: (data: PensionArrangementDetail): JSX.Element => {
                    return <td>{data.Taxable ? "Yes" : "No"}</td>;
                },
            },
        ];
    }

    private getCappedColumns(): any {
        return [
            {
                title: "GAD Maximum",
                key: "GADMaximum",
                render: (data: CappedPensionArrangementDetail): JSX.Element => {
                    return <td>{MoneyDisplayHelper.getMoneyFormat(data.GADMaximum)}</td>;
                },
            },

            {
                title: "Income taken to date",
                key: "Incometakentodate",
                render: (data: CappedPensionArrangementDetail): JSX.Element => {
                    return <td>{MoneyDisplayHelper.getMoneyFormat(data.IncomeTaken)}</td>;
                },
            },
            {
                title: "Last mandatory review",
                key: "Lastmandatoryreview",
                render: (data: CappedPensionArrangementDetail): JSX.Element => {
                    return <td>{JsonDateParser.getFormatDate(data.LastMandatoryReview, "dd/MM/yyyy")}</td>;
                },
            },
        ];
    }
}
